import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { Close, AddCircleOutlineRounded, ArrowUpward, ArrowDownward, ArrowDropDown, Search, CheckCircle, ZoomIn, ZoomOut, Search as SearchIcon, Fullscreen, FullscreenExit, AttachFile } from "@mui/icons-material";
import { Group as PanelGroup, Panel, Separator as PanelResizeHandle } from "react-resizable-panels";
//@ts-ignore
import BpmnViewer from "bpmn-js/lib/NavigatedViewer";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItemButton,
  Paper,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  Step,
  StepLabel,
  Stepper,
  Chip,
  Tooltip,
  SxProps,
  Theme,
} from "@mui/material";
import {
  baseApi,
  flownodesApi,
  processesApi,
  organizationsApi,
  tagsApi,
} from "api";
import dayjs from "dayjs";
import { getTranslation, addStartedByMeProcessId, create_UUID } from "common";
import { toBase64, SUPPORTED_EXTENSIONS, getFileExtension } from "common/utilities";
import { PrioritiesDropDown } from "./PrioritiesDropDown";
import DuplicateCodeDialog, {
  OperationStatusLine,
} from "components/custom-fields/DuplicateCodeDialog";
import type { OperationStatus } from "model/CustomFields";
import { apiError, customFieldsApi } from "api/customFieldsApi";
import {
  canSubmit,
  duplicateCodesFrom,
  isTimeout,
  outcomeOfStatus,
  type StartOutcome,
} from "components/custom-fields/startFlow";
import {
  useBreakpoint,
  useForm,
  useSelectedOrganization,
  useSnackbar,
  useTranslations,
  useUser,
} from "hooks";
import { isRequired, Validation } from "hooks/useForm";
import UserDataFieldsInfo from "components/UserDataFieldsInfo";
import { Priority } from "hooks/usePriorities";
import DropZone from "../DropZone";
import noModel from "../../image/empty-pana.svg";
import { useNavigate } from "react-router-dom";
import LabelManager from "../labels/LabelManager";
import { LabelData } from "components/labels/label_Interface";
import Labels from "components/labels/Labels";
import { SortState } from "hooks/useSortableList";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import { RichTextEditor } from "components/Editor/plugins/RichTextEditor";
import { processmodelsApi } from "api";
import { bpmnDiagramThemeSx } from "utils/bpmnDiagramStyles";
import CustomFieldInput from "components/custom-fields/CustomFieldInput";
import FieldsList from "components/FieldsList";
import { attachmentVerdict, withAttachmentIds } from "components/custom-fields/attachmentField";
import { MAX_ATTACHMENTS } from "common/attachmentLimits";
import { valuesEqual } from "components/custom-fields/legacyAdapter";
import type { FieldValue, RuntimeContext, FieldError } from "model/CustomFields";
import { messageKeyFor } from "components/custom-fields/errorMessages";
import { translateMessage } from "components/custom-fields/messages";
import { UserData } from "hooks/useUserDataKeys";
import SlaStartSection from "components/sla/SlaStartSection";
import useSlaOrganizationEnabled from "components/sla/useSlaOrganizationEnabled";
import {
  SLA_START_PAYLOAD_KEY,
  type SlaStartPayload,
} from "model/slaStartModel";

const INITIAL_STATE = {
  name: "",
  description_detail: "",
  /**
   * Gli userdata ad-hoc di questo avvio: chiavi libere, valide solo per questo
   * processo. Vivono accanto ai campi custom dello schema (che passano da
   * `customFieldChanges`): sono due store distinti del payload.
   */
  meta: {} as { [key: string]: UserData },
  selectedProcess: null,
};

const getBpmnPreviewSx = (isMobile: boolean): SxProps<Theme> => ({
  flex: 1,
  width: "100%",
  height: "100%",
  position: "relative",
  touchAction: isMobile ? "none" : "auto",
  ...bpmnDiagramThemeSx,
  "& .djs-palette": { display: "none" },
  "& .djs-palette-open": { float: "unset" },
  "& .djs-element": { cursor: "grab", pointerEvents: "none" },
  "& .djs-drag-group": { cursor: "grabbing" },
  "& .djs-overlay-context-pad": { display: "none" },
  "& .djs-hit": { pointerEvents: "none" },
  "& .djs-connection .djs-hit": { pointerEvents: "none" },
  "& .djs-shape .djs-hit": { pointerEvents: "none" },
});

interface Process {
  assignmentmode: number;
  id: number;
  idorganization: number;
  idprocessmodel: number;
  idswimlane: number;
  idtype: number;
  name: string;
  processmodelname: string;
  swimlanename: string;
  userschema: string;
  descriptiondetail?: string | null;
}

interface StartProcessProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isAdmin?: boolean;
  priorities?: Priority[];
  onProcessStarted?: () => void | Promise<void>;
}

const StartProcess: React.FC<StartProcessProps> = ({
  open,
  setOpen,
  isAdmin = false,
  priorities,
  onProcessStarted,
}) => {
  const selectedOrganization = useSelectedOrganization();
  const user = useUser();

  /**
   * Lo stato dell'operazione, per il protocollo dei campi personalizzati.
   *
   * Il browser non chiama `prepare`: lo fa il motore, che e' autenticato come
   * servizio. Qui si tiene l'`operationId` dell'intento — cosi' un retry dopo un
   * timeout riprende la **stessa** operazione invece di aprirne una seconda — e
   * l'esito, che e' l'unica cosa che autorizza a dire "avviato".
   */
  const [outcome, setOutcome] = useState<StartOutcome>({ kind: "idle" });
  const [operationId, setOperationId] = useState<string | null>(null);
  const [duplicateCodes, setDuplicateCodes] = useState<string[]>([]);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  /**
   * L'ultimo stato che il server ha dichiarato per l'operazione.
   *
   * Si tiene il valore **grezzo**, non una sua traduzione: `applied` e' cio' che
   * il motore ha risposto, e una frase tradotta che lo dicesse sarebbe una
   * deduzione dell'interfaccia. Mostrarlo com'e' e' l'unica prova che un test
   * puo' leggere senza dipendere da una lingua.
   */
  const [operationStatus, setOperationStatus] = useState<OperationStatus | null>(null);
  /**
   * Il token che lega la conferma alla sfida che l'utente ha visto.
   *
   * Arriva insieme alla sfida, e non si inventa: una conferma con un token
   * vuoto e' una conferma che il server non puo' ricondurre all'analisi
   * mostrata, cioe' un consenso per qualcosa che nessuno ha visto.
   */
  const [confirmationToken, setConfirmationToken] = useState<string | null>(null);
  /**
   * L'ultima richiesta d'avvio inviata, per rimandarla **identica**.
   *
   * Dopo la conferma il motore deve rivedere lo stesso intento: ricostruirlo dal
   * form aperto lo farebbe dipendere da quello che l'utente ha toccato nel
   * frattempo, e sarebbe un secondo intento travestito da conferma.
   */
  const lastStart = useRef<{
    name: string;
    files: File[];
    meta: { [key: string]: UserData };
    fieldFiles: Record<string, File[]>;
    customFieldChanges: Record<string, FieldValue>;
    instanceHandle: string;
    contextId: string;
  } | null>(null);
  const [processes, setProcesses] = useState<Process[]>([]);
  const [selectedProcess, setSelectedProcess] = useState<Process | null>(null);
  /** Apre il dialog "aggiungi campo" di FieldsList: gli userdata ad-hoc. */
  const [openAddFieldDialog, setOpenAddFieldDialog] = useState(false);
  const [startContext, setStartContext] = useState<RuntimeContext | null>(null);
  // Tick locale: l'override inCard sta in localStorage, che non rirenderizza da
  // solo. L'incremento dopo il toggle forza il ricalcolo del `checked`.
  const [startValues, setStartValues] = useState<Record<string, FieldValue>>({});
  /**
   * I valori del **primo** contesto: il termine di paragone dei `changes`.
   *
   * Il ricalcolo e' stateless lato server — riparte dai default e da cio' che
   * il client manda. Il diff sta quindi su questa fotografia e non sul contesto
   * aggiornato: dopo un refresh le dipendenze gia' digitate sono dentro il
   * contesto, e un diff contro di lui le dimenticherebbe — il blur su un campo
   * estraneo le farebbe sparire dalla richiesta, e la formula ricalcolerebbe
   * sul vuoto («Dati mancanti»).
   */
  const initialValuesRef = useRef<Record<string, FieldValue>>({});
  const [fieldFiles, setFieldFiles] = useState<Record<string, File[]>>({});
  const [startHandle, setStartHandle] = useState("");
  const [startContextError, setStartContextError] = useState(false);
  /**
   * I campi che il server ha rifiutato all'avvio (422 `VALIDATION_FAILED` con
   * `fieldErrors`): si evidenzia il campo incriminato e si tiene il dialogo
   * aperto per correggerlo. Prima questi errori erano letti dal contratto ma
   * buttati via, e l'utente non vedeva nulla.
   */
  const [startFieldErrors, setStartFieldErrors] = useState<FieldError[]>([]);
  const [attachmentRecoveryError, setAttachmentRecoveryError] = useState<string | null>(null);
  const [attachmentRecoveryProcessId, setAttachmentRecoveryProcessId] = useState<number | null>(null);
  useEffect(() => {
    if (!open || !selectedProcess) {
      setStartContext(null);
      setStartValues({});
      initialValuesRef.current = {};
      setFieldFiles({});
      setAttachmentRecoveryError(null);
      setAttachmentRecoveryProcessId(null);
      setStartHandle("");
      setStartFieldErrors([]);
      return;
    }
    // A different start event is a different intent; its context and retry ID
    // must not inherit an operation prepared for the previous selection.
    setOperationId(null);
    setOutcome({ kind: "idle" });
    setChallengeId(null);
    lastStart.current = null;
    let active = true;
    const handle = create_UUID();
    setStartHandle(handle);
    setStartContext(null);
    setStartValues({});
    initialValuesRef.current = {};
    setFieldFiles({});
    setAttachmentRecoveryError(null);
    setAttachmentRecoveryProcessId(null);
    setStartContextError(false);
    setStartFieldErrors([]);
    void customFieldsApi.getStartContext(String(selectedProcess.idprocessmodel), {
      operationId: handle,
      instanceHandle: handle,
    }).then((context) => {
      if (!active) return;
      const values = Object.fromEntries(context.fields.map((field) => [field.fieldId, field.value]));
      initialValuesRef.current = values;
      setStartContext(context);
      setStartValues(values);
    }).catch(() => { if (active) setStartContextError(true); });
    return () => { active = false; };
  }, [open, selectedProcess?.id]);

  /**
   * Il ricircolo del contesto all'avvio: il calcolato lo ricalcola il server.
   *
   * Il contesto di avvio si legge una volta sola, quindi una dipendenza
   * modificata dopo (il numero dentro una formula) lascerebbe i calcolati
   * fermi ai valori di prima. Sul blur dei campi si ripete `getStartContext`
   * portando come `changes` il testo **grezzo** digitato: il client non valuta
   * mai una formula, la manda al server che la ricalcola in ordine di
   * dipendenza. Il debounce vive sul blur, non sui tasti: mentre si scrive non
   * parte niente, e il merge lascia intatto cio' che si sta ancora digitando
   * quando la risposta arriva (stessa regola di `acceptSavedContext`).
   */
  const startValuesRef = useRef(startValues);
  const startContextRef = useRef(startContext);
  useEffect(() => { startValuesRef.current = startValues; }, [startValues]);
  useEffect(() => { startContextRef.current = startContext; }, [startContext]);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshSeq = useRef(0);
  useEffect(() => () => {
    if (refreshTimer.current !== null) clearTimeout(refreshTimer.current);
  }, []);
  /**
   * La bozza dopo un ricalcolo: il server vince sui campi che la richiesta non
   * ha visto cambiare, e il testo cambiato **mentre** era in volo resta in
   * bozza — sovrascriverlo sposterebbe il cursore sotto le dita.
   */
  const mergeRefreshedValues = useCallback(
    (
      latest: Record<string, FieldValue>,
      sent: Record<string, FieldValue>,
      refreshed: RuntimeContext
    ) =>
      Object.fromEntries(
        refreshed.fields.map((field) => [
          field.fieldId,
          field.access === "editable" &&
          !valuesEqual(latest[field.fieldId] ?? null, sent[field.fieldId] ?? null)
            ? latest[field.fieldId] ?? null
            : field.value,
        ])
      ),
    []
  );
  const scheduleStartContextRefresh = useCallback(() => {
    const modelId = selectedProcess?.idprocessmodel;
    if (!modelId) return;
    if (refreshTimer.current !== null) clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => {
      refreshTimer.current = null;
      const context = startContextRef.current;
      if (!context) return;
      const latest = startValuesRef.current;
      // Solo i campi editabili davvero cambiati, ma **rispetto alla fotografia
      // iniziale** — non al contesto aggiornato: il server e' stateless, e un
      // diff contro l'ultimo contesto dimenticherebbe le dipendenze gia'
      // digitate (il refresh le ha confermate dentro il contesto), facendo
      // ricalcolare la formula sul vuoto al primo blur su un campo estraneo.
      const initial = initialValuesRef.current;
      const changes = Object.fromEntries(
        context.fields
          .filter(
            (field) =>
              field.access === "editable" &&
              !valuesEqual(latest[field.fieldId] ?? null, initial[field.fieldId] ?? null)
          )
          .map((field) => [field.fieldId, latest[field.fieldId] ?? null])
      );
      if (!Object.keys(changes).length) return;
      const sequence = ++refreshSeq.current;
      void customFieldsApi
        .getStartContext(String(modelId), {
          operationId: startHandle,
          instanceHandle: startHandle,
          changes,
        })
        .then((refreshed) => {
          // Una risposta superata da una richiesta piu' nuova — o da un altro
          // avvio (il dialogo si e' chiuso e riaperto su un modello diverso) —
          // non ribalta lo stato piu' recente.
          if (
            refreshSeq.current !== sequence ||
            startContextRef.current?.contextId !== context.contextId
          ) {
            return;
          }
          setStartContext(refreshed);
          setStartValues(mergeRefreshedValues(startValuesRef.current, changes, refreshed));
        })
        .catch(() => {
          // Un ricalcolo mancato non blocca l'avvio: gli stessi `changes`
          // partono comunque al submit, ed e' il server a ricalcolare la'.
        });
    }, 400);
  }, [selectedProcess?.idprocessmodel, startHandle, mergeRefreshedValues]);
  const [fallbackPriorities, setFallbackPriorities] = useState<Priority[]>([]);
  const [checked, setChecked] = useState<LabelData[]>([]);
  const [selectedPriority, setSelectedPriority] = useState<Priority | null>(null);
  const administrator = true;

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const handleFileChange = (files: File[] | null) => {
    if (!files) return;
    const names = new Set([
      ...files.map((file) => file.name),
      ...Object.values(fieldFiles).flatMap((pending) => pending.map((file) => file.name)),
    ]);
    if (names.size > MAX_ATTACHMENTS) {
      sendSnack({ type: "error", message: getTranslation(translations, "attachment.limit.reached") });
      return;
    }
    setSelectedFiles(files);
  };

  /**
   * Frammento SLA dell'avvio (sez. 5 F2b). Pubblicato da `SlaStartSection` e
   * unito al payload del POST esistente: l'override viaggia **nello stesso**
   * invio, non in una seconda chiamata. `null` = nessuno SLA da inviare.
   */
  const [slaStart, setSlaStart] = useState<SlaStartPayload | null>(null);
  const handleSlaChange = useCallback((payload: SlaStartPayload | null) => {
    setSlaStart(payload);
  }, []);
  const slaEnabled = useSlaOrganizationEnabled(selectedOrganization?.id, isAdmin);

  useEffect(() => {
    if (!slaEnabled) setSlaStart(null);
  }, [slaEnabled]);

  const [isLabelManagerShown, setIsLabelManagerShown] = useState<boolean>(false);
  const descriptionDetailRef = useRef<string>("");
  const pasteCaptureRef = useRef<HTMLInputElement>(null);
  const handleLabelManagerClose = () => setIsLabelManagerShown(false);

  const { sendSnack } = useSnackbar();
  const translations = useTranslations();
  /**
   * Il messaggio da mostrare sotto un campo rifiutato dal server, o `null`.
   *
   * La chiave risolta dal contratto (`messageKey`) vince sul codice; se manca si
   * ricade sulla mappa codice→chiave del client.
   */
  const errorForField = useCallback(
    (fieldId: string): string | null => {
      const fe = startFieldErrors.find((item) => item.fieldId === fieldId);
      if (!fe) return null;
      // `getTranslation` non interpola: i parametri del rifiuto — `{min}` e
      // `{max}` di un fuori intervallo — si sostituiscono qui. Se la chiave non
      // ha segnaposto resta com'e', e se un parametro manca il template si
      // mostra con il segnaposto visibile invece di sparire.
      return translateMessage(
        getTranslation(translations, fe.messageKey || messageKeyFor({ code: fe.code })),
        fe.params as Record<string, string | number>
      );
    },
    [startFieldErrors, translations]
  );
  /** L'utente sta correggendo: l'errore di quel campo non e' piu' attendibile. */
  const clearFieldError = useCallback(
    (fieldId: string) =>
      setStartFieldErrors((previous) => previous.filter((fe) => fe.fieldId !== fieldId)),
    []
  );
  const stageFieldFiles = (fieldId: string, picked: FileList | null) => {
    if (!picked?.length) return;
    const supported = Array.from(picked).filter((file) => {
      const extension = getFileExtension(file.name);
      return extension && SUPPORTED_EXTENSIONS.includes(extension.toLowerCase());
    });
    if (supported.length !== picked.length) {
      sendSnack({ type: "error", message: getTranslation(translations, "attachment.extension.unsupported") });
    }
    const previous = fieldFiles[fieldId] ?? [];
    const verdict = attachmentVerdict(supported, previous.map((file) => file.name), {
      existing: 0, using: 0, permit: 0, bypass: true,
    });
    const staged = [...previous, ...verdict.accepted];
    const allNames = new Set([
      ...selectedFiles.map((file) => file.name),
      ...Object.values({ ...fieldFiles, [fieldId]: staged }).flatMap((files) => files.map((file) => file.name)),
    ]);
    if (allNames.size > MAX_ATTACHMENTS) {
      sendSnack({ type: "error", message: getTranslation(translations, "attachment.limit.reached") });
      return;
    }
    for (const duplicate of verdict.duplicated) {
      sendSnack({ type: "info", message: `${getTranslation(translations, "attachment.already.present")}: ${duplicate.name}` });
    }
    if (verdict.accepted.length) clearFieldError(fieldId);
    setFieldFiles((current) => ({ ...current, [fieldId]: staged }));
  };
  const validations: Validation[] = [
    ({ name }) =>
      isRequired(name) || { name: getTranslation(translations, "register.fieldrequired") },
  ];

  const { changeHandler, reset, values, errors, touched, isValid } = useForm(INITIAL_STATE, validations);
  const navigate = useNavigate();
  const isMobile = useBreakpoint("down", "md");

  const listRef = useRef<HTMLUListElement>(null);
  const itemRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const fullscreenRef = useRef<HTMLDivElement>(null);

  // Wizard step state
  const [step, setStep] = useState<number>(0);
  const [isDescriptionFocused, setIsDescriptionFocused] = useState<boolean>(false);
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);

  // Persist panel layouts in localStorage
  const STEP1_LAYOUT_KEY = "startprocess-step1-layout";
  const STEP2_LAYOUT_KEY = "startprocess-step2-layout";
  const loadLayout = (key: string): { [id: string]: number } | undefined => {
    try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : undefined; } catch { return undefined; }
  };
  const saveLayout = (key: string, layout: { [id: string]: number }) => {
    try { localStorage.setItem(key, JSON.stringify(layout)); } catch {}
  };

  // Calcola larghezza iniziale colonna lista in base al nome più lungo
  const step1DefaultLayout = useMemo(() => {
    const saved = loadLayout(STEP1_LAYOUT_KEY);
    if (saved) return saved;

    if (!processes.length) return undefined;

    const longestLabel = processes.reduce((longest, p) => {
      const label = p.name === "" ? p.processmodelname : `${p.processmodelname} (${p.name})`;
      return label.length > longest.length ? label : longest;
    }, "");

    // Misura con canvas per accuratezza
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    let textWidth = longestLabel.length * 8;
    if (ctx) {
      ctx.font = '14px "Poppins", "Roboto", sans-serif';
      textWidth = ctx.measureText(longestLabel).width;
    }

    // Padding: scrollbar (~16px), item padding (~32px), check icon (~24px), margins
    const neededPx = textWidth + 80;
    // Assume dialog width ~1200px (maxWidth xl can be larger but this is a safe default)
    const leftPct = Math.min(Math.max((neededPx / 1200) * 100, 18), 40);
    return { "step1-list": leftPct, "step1-preview": 100 - leftPct };
  }, [processes]);

  const scrollToSelectedProcess = useCallback(() => {
    if (selectedProcess && itemRefs.current[selectedProcess.id]) {
      itemRefs.current[selectedProcess.id]?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [selectedProcess]);

  const [loading, setLoading] = useState<boolean>(false);
  const [sortOrder, setSortOrder] = useState<SortState>(SortState.Asc);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const sortProcessesAsc = () => {
    setProcesses([...processes].sort((a, b) => a.processmodelname.localeCompare(b.processmodelname)));
    setSortOrder(SortState.Asc);
  };
  const sortProcessesDesc = () => {
    setProcesses([...processes].sort((a, b) => b.processmodelname.localeCompare(a.processmodelname)));
    setSortOrder(SortState.Desc);
  };

  useEffect(() => {
    if (!open || !selectedOrganization?.id) {
      return;
    }

    (async () => {
      try {
        const res = await baseApi.get(`startevents/organizations/${selectedOrganization.id}`);
        const sortedProcesses = res.data.possiblestart.sort(
          (a: { processmodelname: string }, b: { processmodelname: any }) =>
            a.processmodelname.localeCompare(b.processmodelname)
        );
        setProcesses(sortedProcesses);
      } catch { }
    })();
  }, [open, selectedOrganization?.id]);

  useEffect(() => {
    if (!open) {
      setChecked([]);
      reset();
      setSelectedFiles([]);
      descriptionDetailRef.current = "";
      setSearchQuery("");
      setStep(0);
      setIsDescriptionFocused(false);
      setIsFullScreen(false);
      try {
        if (document.fullscreenElement) document.exitFullscreen();
        window.screen.orientation?.unlock?.();
      } catch {}
    }
  }, [processes, open, reset]);

  const filteredProcesses = useMemo(() => {
    if (!searchQuery.trim()) return processes;
    const query = searchQuery.toLowerCase();
    return processes.filter((process) => {
      const processName = process.name === ""
        ? process.processmodelname
        : `${process.processmodelname} (${process.name})`;
      return processName.toLowerCase().includes(query);
    });
  }, [processes, searchQuery]);

  const fetchOrganizationPriorities = useCallback(async () => {
    if (!selectedOrganization?.id) return;
    const response = await organizationsApi.get(`/${selectedOrganization?.id}/priorities`);
    if (response?.data?.priorities) {
      setFallbackPriorities(response.data.priorities);
    }
  }, [selectedOrganization]);

  useEffect(() => {
    if (open && !priorities) {
      fetchOrganizationPriorities();
    }
  }, [fetchOrganizationPriorities, open, priorities]);

  const allPriorities = priorities || fallbackPriorities;

  useEffect(() => {
    if (selectedOrganization?.id && allPriorities.length > 0 && !selectedPriority) {
      const defaultPriority = allPriorities[0];
      if (defaultPriority) setSelectedPriority(defaultPriority);
    }
  }, [selectedOrganization, allPriorities, selectedPriority]);

  const LAST_SELECTED_PROCESS_KEY = "lastSelectedProcess";
  const saveLastSelectedProcess = (process: any) => {
    try {
      localStorage.setItem(LAST_SELECTED_PROCESS_KEY, JSON.stringify({ id: process.id, name: process.name, processmodelname: process.processmodelname, timestamp: Date.now() }));
    } catch { }
  };
  const getLastSelectedProcess = () => {
    try {
      const saved = localStorage.getItem(LAST_SELECTED_PROCESS_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  };

  useEffect(() => {
    if (selectedProcess) saveLastSelectedProcess(selectedProcess);
  }, [selectedProcess]);

  useEffect(() => {
    if (open && processes.length > 0 && selectedProcess && itemRefs.current[selectedProcess.id]) {
      requestAnimationFrame(() => requestAnimationFrame(() => scrollToSelectedProcess()));
    }
  }, [open, processes.length, selectedProcess?.id, scrollToSelectedProcess]);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => scrollToSelectedProcess(), 300);
      return () => clearTimeout(timer);
    }
  }, [open, scrollToSelectedProcess]);

  useEffect(() => {
    if (processes.length === 0) { setSelectedProcess(null); return; }
    if (processes.length === 1) {
      const singleProcess = processes[0];
      if (!selectedProcess || selectedProcess.id !== singleProcess.id) setSelectedProcess(singleProcess);
      return;
    }
    // Scelta valida solo se il modello è ancora in lista (aperture precedenti,
    // altra organizzazione, o modello non più avviabile): altrimenti si ripiega
    // sul primo modello. Senza il fallback, con più modelli — es. start assegnato
    // a un gruppo o ruolo dell'utente — la lista restava senza focus.
    if (!selectedProcess || !processes.some((p) => p.id === selectedProcess.id)) {
      const lastSelected = getLastSelectedProcess();
      const matchingProcess = lastSelected && processes.find((p) => p.id === lastSelected.id);
      setSelectedProcess(matchingProcess || processes[0]);
    }
  }, [processes, selectedProcess]);

  // BPMN Preview
  const bpmnContainerRef = useRef<HTMLDivElement>(null);
  const bpmnViewerRef = useRef<any>(null);

  const openDiagram = useCallback(async (xml: string) => {
    const container = bpmnContainerRef.current;
    if (!container) return;
    if (bpmnViewerRef.current) {
      try { bpmnViewerRef.current.destroy(); } catch { }
    }
    container.innerHTML = "";
    const viewer = new BpmnViewer({ container, readOnly: true, additionalModules: [] });
    bpmnViewerRef.current = viewer;
    try {
      await viewer.importXML(xml);
      (viewer.get("canvas") as any).zoom("fit-viewport");
      setZoomLevel(1);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (step === 0 && selectedProcess) {
      // rAF ensures the Dialog has finished rendering and the ref is attached
      const raf = requestAnimationFrame(() => {
        (async () => {
          try {
            const { data } = await processmodelsApi.get(`/${selectedProcess.idprocessmodel}`);
            if (data.model) openDiagram(data.model);
          } catch { }
        })();
      });
      return () => cancelAnimationFrame(raf);
    }
    if (step === 0 && !selectedProcess && bpmnContainerRef.current) {
      bpmnContainerRef.current.innerHTML = "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProcess, step, openDiagram, open, isMobile, isFullScreen]);

  // Cleanup viewer on unmount
  useEffect(() => {
    return () => {
      if (bpmnViewerRef.current) {
        try { bpmnViewerRef.current.destroy(); } catch { }
      }
    };
  }, []);

  // Capture Ctrl+V globally so the user can paste a file from
  // anywhere in the dialog (not only when an input has focus).
  // The browser does not generate a `paste` event for non-editable
  // targets, so we intercept the keydown, focus a hidden input and
  // call `execCommand("paste")` to synthesise a paste event on
  // that input. The event then bubbles naturally to `document`,
  // where the existing DropZone listener picks it up and runs the
  // upload with the proper date+time filename.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const isPaste = (e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "v";
      if (!isPaste) return;
      const target = e.target as HTMLElement | null;
      if (target?.matches?.("input, textarea, [contenteditable='true'], [contenteditable='']")) {
        return;
      }
      e.preventDefault();
      const el = pasteCaptureRef.current;
      if (!el) return;
      el.focus();
      setTimeout(() => {
        try {
          document.execCommand("paste");
        } catch {
          /* ignore */
        }
      }, 0);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  /**
   * La conferma quando dei codici sono gia' usati.
   *
   * E' la transizione **dello stesso** intento: lo stesso `operationId`, non uno
   * nuovo. Aprirne uno diverso significherebbe un secondo avvio travestito da
   * conferma.
   *
   * Il confirm risponde `prepared`, **non** `applied`: minta il ticket per il
   * motore, che lo consuma quando rivede la richiesta d'avvio. Dire "avviato"
   * qui sarebbe la bugia che questo dialogo esiste per impedire — quindi la
   * richiesta si rimanda, identica, ed e' il suo esito a dire se e' partito.
   */
  const handleConfirmDuplicate = async () => {
    if (!operationId || !challengeId) return;

    setOutcome({ kind: "working", stage: "confirming" });
    try {
      const esito = await customFieldsApi.confirmOperation(operationId, {
        challengeId,
        confirmationToken: confirmationToken ?? "",
      });

      setOperationStatus(esito.status);
      setOutcome(outcomeOfStatus(esito.status, esito.resultPreview ?? null));

      // Il motore consuma il ticket quando rivede l'intento: senza questo
      // secondo invio il numero resta riservato e il processo non parte mai.
      await risottometti();
      // La sfida **non** si chiude da sola, nemmeno su un esito definitivo: e'
      // l'unico posto dove si legge che cosa e' successo davvero, e i codici in
      // conflitto restano visibili accanto all'esito. `rejected` compreso —
      // chiuderla lascerebbe l'utente convinto che sia partito.
    } catch (e) {
      const codice = apiError(e)?.code ?? null;
      if (codice === "CONFIRMATION_STALE") {
        // Il consenso era per un'analisi che non vale piu'. Si riapre una
        // conferma nuova — cioe' si rimanda l'intento e si mostra la sfida che
        // il server apre adesso — invece di riusare quella scaduta.
        setOutcome({ kind: "refused", code: "CONFIRMATION_STALE" });
        await risottometti();
        return;
      }
      setOutcome({ kind: "refused", code: codice ?? "CONFIRMATION_STALE" });
    }
  };

  /**
   * Rimanda l'ultima richiesta d'avvio, identica.
   *
   * Serve dopo la conferma: il ticket e' del motore, e il processo parte quando
   * il motore lo consuma. Un payload diverso sarebbe un secondo intento — qui
   * si riusa quello che l'utente ha gia' compilato.
   */
  const risottometti = async () => {
    if (lastStart.current) await handleStartProcess(lastStart.current);
  };

  /**
   * Il token su cui si agganciano gli allegati dell'avvio.
   *
   * `attachments/light` — l'unica lettura degli allegati dell'app — mostra solo
   * i file legati a un **token del processo**: la rotta
   * `processes/{idprocess}/attachments` li scrive con `idprocesstoken = 0` e li
   * rende invisibili a ogni superficie. Qui si carica quindi sulla stessa rotta
   * del dialog del task (`flownodes/{idprocesstoken}/attachments`), col token
   * dello start event che l'avvio ha in mano (`previousprocesstoken`). Il
   * recupero (timeout, retry dal banner) non ha la risposta: si rilegge.
   */
  const startTokenId = async (processId: number): Promise<number | null> => {
    try {
      const { data } = await processesApi.get(`${processId}/tokens`);
      const tokens: { id: number; flownodetype: number }[] = data.processtokens ?? [];
      const start = tokens.find((token) => token.flownodetype === 0) ?? tokens[0];
      return start?.id ?? null;
    } catch {
      return null;
    }
  };

  const saveStartAttachments = async (
    processId: number,
    general: File[],
    pending: Record<string, File[]>,
    tokenId?: number | null
  ) => {
    const names = new Map(general.map((file) => [file.name, file]));
    for (const files of Object.values(pending)) {
      for (const file of files) names.set(file.name, file);
    }
    if (!names.size) return;

    const list = async (): Promise<{ id: number; filename: string }[]> => {
      const { data } = await processesApi.get(`${processId}/attachments/light`);
      return data.attachments ?? [];
    };
    const already = new Set((await list()).map((item) => item.filename));
    const token = tokenId ?? (await startTokenId(processId));
    if (!token) throw new Error(`Nessun token del processo ${processId} per gli allegati`);
    for (const file of Array.from(names.values())) {
      if (already.has(file.name)) continue;
      await flownodesApi.post(`${token}/attachments`, {
        idorganization: selectedOrganization?.id,
        filename: file.name,
        cloudlink: "",
        idtype: 1,
        description: file.name,
        blob: await toBase64(file),
        size: file.size,
      });
    }
    if (!Object.values(pending).some((files) => files.length)) return;
    const ids = new Map((await list()).map((item) => [item.filename, item.id]));
    // Il contesto va letto **sull'elemento dell'avvio**, non su quello in cui il
    // processo si trova adesso: il gate per-elemento risolve i campi a visibilità
    // `tasks`/`lanes` contro il task corrente, e dopo l'avvio il token è già
    // passato oltre. Un campo visibile solo nello start event sparirebbe dal
    // contesto, il controllo di accesso qui sotto lo dichiarerebbe non più
    // editabile e l'id non verrebbe mai agganciato — il file resterebbe orfano.
    // `taskId` è l'id del token (non il designerid): è lo stesso token su cui è
    // stato caricato il file.
    const context = await customFieldsApi.getContext(String(processId), {
      taskId: String(token),
    });
    const changes: Record<string, string[]> = {};
    for (const [fieldId, files] of Object.entries(pending)) {
      if (!files.length) continue;
      const field = context.fields.find((item) => item.fieldId === fieldId);
      if (field?.access !== "editable") throw new Error(`Attachment field ${fieldId} is no longer editable`);
      const fileIds = files.map((file) => ids.get(file.name));
      if (fileIds.some((id) => id === undefined)) throw new Error(`Attachment ID missing for ${fieldId}`);
      changes[fieldId] = withAttachmentIds(
        Array.isArray(field.value) ? field.value : [],
        fileIds as number[]
      );
    }
    await customFieldsApi.patchValues(String(processId), {
      operationId: create_UUID(),
      contextId: context.contextId,
      expectedRevision: context.revision,
      changes,
    });
  };

  const recoverStartAttachments = async () => {
    if (!operationId || !lastStart.current) return;
    setLoading(true);
    try {
      const result = attachmentRecoveryProcessId ? null : await customFieldsApi.getOperation(operationId);
      if (result && result.status !== "applied") throw new Error("Start operation is not applied");
      const processId = attachmentRecoveryProcessId ?? Number(result?.resultPreview?.processId);
      if (!Number.isSafeInteger(processId) || processId <= 0) {
        throw new Error("Applied operation has no processId");
      }
      await saveStartAttachments(processId, lastStart.current.files, lastStart.current.fieldFiles);
      setAttachmentRecoveryError(null);
      setAttachmentRecoveryProcessId(null);
      sendSnack({ type: "success", message: getTranslation(translations, "generic.fileuploadedsuccessfully") });
      reset();
      setSelectedProcess(null);
      setOpen(false);
      setOperationId(null);
      await onProcessStarted?.();
    } catch (error) {
      console.error("Impossibile recuperare gli allegati dopo l'avvio", error);
      setAttachmentRecoveryError("Il processo è avviato, ma gli allegati non sono salvati. Riprova il salvataggio.");
    } finally {
      setLoading(false);
    }
  };

  const handleStartProcess = async ({ name, files, meta: previousMeta, fieldFiles: previousFieldFiles, customFieldChanges: previousChanges, instanceHandle: previousHandle, contextId: previousContextId }: {
    name: string;
    files: File[];
    meta?: { [key: string]: UserData };
    fieldFiles?: Record<string, File[]>;
    customFieldChanges?: Record<string, FieldValue>;
    instanceHandle?: string;
    contextId?: string;
  }) => {
    setLoading(true);
    // Un nuovo tentativo azzera gli errori del precedente: si riparte puliti.
    setStartFieldErrors([]);
    // Il confronto e' con la fotografia del **primo** contesto, non con quello
    // aggiornato: il refresh del contesto riporta i valori digitati, e un diff
    // contro di esso risulterebbe vuoto — il server riceverebbe `changes` senza
    // i campi appena compilati e l'istanza partirebbe con i soli default.
    const customFieldChanges = previousChanges ?? Object.fromEntries(
      (startContext?.fields ?? [])
        .filter(
          (field) =>
            field.access === "editable" &&
            !valuesEqual(
              startValues[field.fieldId],
              initialValuesRef.current[field.fieldId]
            )
        )
        .map((field) => [field.fieldId, startValues[field.fieldId]])
    );
    // Gli userdata ad-hoc sono un campo **distinto** da `customFieldChanges`:
    // viaggiano insieme nel payload ma in due store separati del processo.
    const meta = previousMeta ?? values.meta ?? {};
    const instanceHandle = previousHandle ?? startHandle;
    const contextId = previousContextId ?? startContext?.contextId ?? "";
    const pendingFieldFiles = previousFieldFiles ?? fieldFiles;
    // L'ultima richiesta, per rimandarla identica dopo la conferma: ricostruirla
    // dal form aperto la farebbe dipendere da cio' che l'utente ha toccato nel
    // frattempo.
    lastStart.current = { name, files, meta, fieldFiles: pendingFieldFiles, customFieldChanges, instanceHandle, contextId };
    // Un `operationId` per **intento**: se il submit viene ritentato dopo un
    // timeout, riprende la stessa operazione invece di aprirne una seconda. E'
    // cio' che rende sicuro il retry, e il browser lo genera una volta sola.
    const operation = operationId ?? instanceHandle ?? create_UUID();
    setOperationId(operation);
    setOutcome({ kind: "working", stage: "preparing" });
    // Se il dialogo si chiude lo decide l'esito, non il `finally`. Lo stato
    // `outcome` letto la' sotto e' quello del render precedente: chiudere su
    // quello significherebbe chiudere anche su una conferma appena aperta —
    // cioe' perdere la domanda che l'utente deve ancora vedere.
    let closeDialog = true;

    /**
     * true solo dopo che il POST di avvio e' andato a buon fine. Prima era
     * `finally` incondizionato: un 403/409/422 chiudeva il dialog e azzerava la
     * configurazione, mentre il criterio F2b chiede che una revisione divergente
     * **non** faccia perdere la configurazione e non avvii nulla (sez. 5).
     */
    let started = false;
    try {
      const payload = {
        idprocessmodel: selectedProcess?.idprocessmodel,
        idorganization: selectedOrganization?.id,
        idevent: selectedProcess?.id,
        startdate: dayjs().utc().toISOString(),
        enddate: null,
        description: name,
        descriptiondetail: descriptionDetailRef.current || null,
        userdata: meta,
        // Frammento SLA nello stesso POST: nessuna seconda richiesta.
        ...(slaStart ? { [SLA_START_PAYLOAD_KEY]: slaStart } : {}),
        customFieldChanges,
        instanceHandle,
        contextId,
        // L'intento che il motore inoltra al protocollo. Il browser non chiama
        // `prepare`: lo fa il motore, che e' autenticato come servizio.
        operationId: operation,
      };
      // Il timeout e' **per richiesta**, non globale: `processesApi` non ne ha
      // uno, e senza questo un motore che non risponde lascerebbe il modulo in
      // caricamento per sempre — con il numero riservato e nessuna risposta da
      // leggere. E' anche cio' che rende raggiungibile il recupero dell'esito:
      // senza, il ramo del timeout non si attiva mai.
      const { data } = await processesApi.post("", payload, { timeout: 30000 });
      // Da qui in poi si e' **davvero** avviato. Fino a un attimo fa no: il
      // numero era riservato e niente di BPM era avvenuto. Le operazioni che
      // seguono registrano un processo che esiste.
      setOutcome({ kind: "started", resultPreview: null });
      // La risposta del motore e' l'`applied` dell'operazione: e' il motore a
      // inoltrarla al protocollo, ed e' arrivata.
      setOperationStatus("applied");

      started = true;
      // Record this process as started by the current user so the
      // "Creati da me" filter can match it. Persisted in localStorage
      // per (userId, orgId) until the BE exposes `idstartuser`.
      if (data?.process?.id && user?.userId && selectedOrganization?.id) {
        addStartedByMeProcessId(
          user.userId,
          selectedOrganization.id,
          data.process.id
        );
      }
      const processId = data.process.id;
      try {
        // Il token dello start event e' quello che il motore ha appena chiuso:
        // gli allegati scelti nell'avvio appartengono a quell'evento.
        await saveStartAttachments(processId, files, pendingFieldFiles, data.previousprocesstoken?.id);
        if (files.length || Object.values(pendingFieldFiles).some((items) => items.length)) {
          sendSnack({ type: "success", message: getTranslation(translations, "generic.fileuploadedsuccessfully") });
        }
      } catch (error) {
        console.error("Impossibile salvare gli allegati dei campi del processo", error);
        const message = `Processo ${processId} avviato, ma allegati non salvati. Riprova il salvataggio.`;
        setAttachmentRecoveryProcessId(processId);
        setAttachmentRecoveryError(message);
        sendSnack({ type: "error", message });
        closeDialog = false;
      }
      try {
        await Promise.all(checked.map((label) =>
          tagsApi.post(`/${selectedOrganization?.id}/processes/${processId}/tags/${label.id}`)
        ));
        if (selectedPriority?.id && data.processtoken) {
          await flownodesApi.post(`/${data.processtoken.idprocess}/priorities/${selectedPriority.id}`, {});
        }
      } catch (error) {
        console.error("Processo avviato, ma etichette o priorità non salvate", error);
        sendSnack({ type: "error", message: `Processo ${processId} avviato, ma etichette o priorità non salvate.` });
      }
    } catch (e: any) {
      // `DUPLICATE_CONFIRMATION_REQUIRED` non e' un guasto: e' la domanda che
      // il motore fa quando dei numeri sono gia' usati. Si mostra la domanda
      // invece di un errore, e si tiene l'`operationId`: la conferma e' la
      // transizione **dello stesso** intento, non uno nuovo.
      // Il contratto: `{error:{code,messageKey,params}}`. `error` e' un
      // **oggetto**: leggerlo come stringa dava "[object Object]", e nessun
      // confronto con un codice scattava.
      const errore = apiError(e);
      const codice = errore?.code;
      const dati = errore?.params ?? null;

      if (codice === "DUPLICATE_CONFIRMATION_REQUIRED") {
        setDuplicateCodes(duplicateCodesFrom(dati));
        setChallengeId((dati?.challengeId as string) ?? null);
        setConfirmationToken((dati?.confirmationToken as string) ?? null);
        setOutcome({ kind: "needs_confirmation", codes: [], anomalies: 0 });
        // Il dialogo resta aperto: l'utente deve decidere, e la sfida non
        // sopravvive alla chiusura del modulo che l'ha aperta.
        closeDialog = false;
        return;
      }

      // Un timeout non e' un rifiuto. La richiesta puo' essere arrivata e il
      // motore aver avviato: ripreparare alla cieca aprirebbe un **secondo**
      // intento con un numero nuovo. Si chiede l'esito dello stesso.
      if (isTimeout(e)) {
        setOutcome({ kind: "unknown", operationId: operation });
        try {
          const esito = await customFieldsApi.getOperation(operation);
          setOperationStatus(esito.status);
          setOutcome(outcomeOfStatus(esito.status, esito.resultPreview ?? null));
          if (esito.status === "applied") {
            if (files.length || Object.values(pendingFieldFiles).some((items) => items.length)) {
              const recoveredId = Number(esito.resultPreview?.processId);
              if (!Number.isSafeInteger(recoveredId) || recoveredId <= 0) {
                setAttachmentRecoveryError("Il processo è avviato, ma gli allegati non sono salvati. Riprova il salvataggio.");
                closeDialog = false;
                return;
              }
              try {
                await saveStartAttachments(recoveredId, files, pendingFieldFiles);
                sendSnack({ type: "success", message: getTranslation(translations, "generic.fileuploadedsuccessfully") });
              } catch (error) {
                console.error("Impossibile salvare gli allegati dopo un timeout", error);
                setAttachmentRecoveryProcessId(recoveredId);
                setAttachmentRecoveryError(`Processo ${recoveredId} avviato, ma allegati non salvati. Riprova il salvataggio.`);
                closeDialog = false;
              }
            }
            return;
          }
        } catch {
          // Nemmeno l'esito si e' potuto leggere. Si resta su `unknown`: tiene
          // l'id per il retry e **non** dichiara partito niente.
          setOutcome({ kind: "unknown", operationId: operation });
        }
        // Il modulo resta aperto col suo id: il retry e' lo stesso intento, e
        // non si riprepara un'intenzione nuova su un numero gia' riservato.
        closeDialog = false;
        return;
      }

      // Un rifiuto con `fieldErrors` riguarda dei campi precisi: si evidenzia il
      // campo incriminato e si **tiene aperto** il dialogo per correggerlo,
      // invece di buttare via gli errori e mostrare solo un rifiuto generico.
      if (errore?.fieldErrors?.length) {
        const fieldErrors = errore.fieldErrors;
        setStartFieldErrors(fieldErrors);
        // E' un rifiuto arrivato e non un avvio in corso: si riabilita il
        // submit. Lasciare `working` (il `finally` non lo azzera quando il
        // dialogo resta aperto) disabiliterebbe il pulsante di Start **per
        // sempre** — `canSubmit` ammette solo `idle` e `refused` — anche dopo
        // che l'utente ha corretto i valori. Timeout e duplicati restano
        // `working`, e li' e' voluto.
        setOutcome({ kind: "refused", code: codice ?? "VALIDATION_FAILED" });
        // Il messaggio top-level resta come fallback, ma solo se nessun campo
        // del modulo corrisponde: altrimenti l'errore e' gia' sotto il campo.
        const suUnCampo = (startContext?.fields ?? []).some((f) =>
          fieldErrors.some((fe) => fe.fieldId === f.fieldId)
        );
        if (!suUnCampo) {
          sendSnack({ type: "error", message: getTranslation(translations, messageKeyFor(errore)) });
        }
        closeDialog = false;
        return;
      }

      // Un'origine automatica non ha nessuno che possa approvare: un rifiuto
      // per essa e' una notifica, mai un dialogo.
      setOutcome(
        codice
          ? { kind: "refused", code: codice }
          : { kind: "refused", code: "VALIDATION_FAILED" }
      );
    } finally {
      // La chiusura avviene solo se **non** si sta aspettando una decisione:
      // chiudere su una conferma pendente perderebbe la domanda.
      if (closeDialog) {
        if (started) {
        reset();
          setSelectedProcess(null);
          setSelectedPriority(null);
          setSlaStart(null);
        setOpen(false);
        // L'intento e' **concluso**. Il prossimo avvio e' un intento nuovo, e
        // riusarne l'id lo farebbe passare per un retry dello stesso: il server
        // risponderebbe `OPERATION_ID_CONFLICT` — o, peggio, riprenderebbe
        // l'operazione di prima invece di avviare il processo che l'utente ha
        // appena chiesto.
        setOperationId(null);
        setOutcome({ kind: "idle" });
        setOperationStatus(null);
        setConfirmationToken(null);
        setDuplicateCodes([]);
        setChallengeId(null);
        setStartFieldErrors([]);
          await onProcessStarted?.();
      }
      }
      setLoading(false);
    }
  };

  const processLabel = (p: Process) =>
    p.name === "" ? p.processmodelname : `${p.processmodelname} (${p.name})`;

  const renderStep1List = () => (
    <>
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider" }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder={getTranslation(translations, "pm.search.model")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
              sx: { borderRadius: 5 },
            }}
          />
          {filteredProcesses.length > 0 && (
            <IconButton
              onClick={sortOrder === SortState.Asc ? sortProcessesDesc : sortProcessesAsc}
              size="small"
              sx={{ borderRadius: 2 }}
            >
              {sortOrder === SortState.Asc ? <ArrowDownward /> : <ArrowUpward />}
            </IconButton>
          )}
        </Stack>
      </Box>
      <List
        ref={listRef}
        id="process-list"
        sx={{
          flex: 1,
          overflow: "auto",
          py: 0.5,
          px: 1,
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(0,0,0,0.2)", borderRadius: "4px" },
        }}
      >
        {filteredProcesses.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
            {searchQuery
              ? getTranslation(translations, "pm.no.processes.found") || "Nessun processo trovato"
              : getTranslation(translations, "pm.no.processes.available") || "Nessun processo disponibile"}
          </Typography>
        ) : (
          filteredProcesses.map((process) => (
            <ListItemButton
              ref={(el) => (itemRefs.current[process.id] = el)}
              key={process.id}
              id={`startprocess-fields-${process.id}`}
              selected={selectedProcess?.id === process.id}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                py: 1,
                "&.Mui-selected": {
                  backgroundColor: (_) => `${_.palette.primary.light}55`,
                  "&:hover": {
                    backgroundColor: (_) => `${_.palette.primary.light}55`,
                  },
                },
              }}
              onClick={() => {
                if (selectedProcess?.id !== process.id) {
                  setSelectedProcess(process);
                  saveLastSelectedProcess(process);
                }
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                <Typography variant="body2" noWrap sx={{ flex: 1, mr: 1 }}>
                  {processLabel(process)}
                </Typography>
                {selectedProcess?.id === process.id && (
                  <CheckCircle sx={{ color: "primary.main", fontSize: 16, flexShrink: 0 }} />
                )}
              </Stack>
            </ListItemButton>
          ))
        )}
      </List>
    </>
  );

  const renderStep1Preview = () => (
    <>
      {selectedProcess ? (
        <>
          <Box
            ref={isFullScreen ? undefined : bpmnContainerRef}
            sx={getBpmnPreviewSx(isMobile)}
          />
          {/* Zoom controls */}
          <Box sx={{ position: "absolute", bottom: isMobile ? 48 : 12, right: 12, display: "flex", alignItems: "center", gap: 0.5, bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 5, px: isMobile ? 1.5 : 1, py: isMobile ? 1 : 0.5, zIndex: 10 }}>
            <IconButton
              size={isMobile ? "medium" : "small"}
              sx={{ borderRadius: 2, p: isMobile ? 1 : 0.5 }}
              onClick={() => {
                const newZoom = Math.max(0.2, parseFloat((zoomLevel - 0.15).toFixed(2)));
                bpmnViewerRef.current?.get("canvas").zoom(newZoom);
                setZoomLevel(newZoom);
              }}
            >
              <ZoomOut fontSize={isMobile ? "medium" : "small"} />
            </IconButton>
            <Tooltip title={getTranslation(translations, "pm.startprocess.fit_viewport") || "Adatta allo schermo"}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", px: 0.5 }}
                onClick={() => {
                  bpmnViewerRef.current?.get("canvas").zoom("fit-viewport");
                  setZoomLevel(1);
                }}
              >
                <SearchIcon sx={{ fontSize: isMobile ? 18 : 14, color: "text.secondary" }} />
                <Typography variant="caption" sx={{ minWidth: 32, textAlign: "center", fontWeight: 600, fontSize: isMobile ? "0.8rem" : "inherit" }}>
                  {Math.round(zoomLevel * 100)}%
                </Typography>
              </Box>
            </Tooltip>
            <IconButton
              size={isMobile ? "medium" : "small"}
              sx={{ borderRadius: 2, p: isMobile ? 1 : 0.5 }}
              onClick={() => {
                const newZoom = Math.min(3.0, parseFloat((zoomLevel + 0.15).toFixed(2)));
                bpmnViewerRef.current?.get("canvas").zoom(newZoom);
                setZoomLevel(newZoom);
              }}
            >
              <ZoomIn fontSize={isMobile ? "medium" : "small"} />
            </IconButton>
            <Box sx={{ height: isMobile ? 20 : 16, width: "1px", bgcolor: "divider", mx: 0.5 }} />
            <Tooltip title={isFullScreen ? "Esci da schermo intero" : "Schermo intero"}>
              <IconButton
                size={isMobile ? "medium" : "small"}
                sx={{ borderRadius: 2, p: isMobile ? 1 : 0.5 }}
                onClick={async () => {
                  const next = !isFullScreen;
                  setIsFullScreen(next);
                  if (isMobile && next) {
                    // small delay so the overlay renders before requesting fullscreen
                    requestAnimationFrame(async () => {
                      try {
                        await fullscreenRef.current?.requestFullscreen?.();
                      } catch {}
                    });
                  }
                }}
              >
                {isFullScreen ? <FullscreenExit fontSize={isMobile ? "medium" : "small"} /> : <Fullscreen fontSize={isMobile ? "medium" : "small"} />}
              </IconButton>
            </Tooltip>
          </Box>
        </>
      ) : (
        <Stack flex={1} alignItems="center" justifyContent="center" spacing={1} sx={{ height: "100%" }}>
          <Typography variant="body2" color="text.secondary">
            {getTranslation(translations, "pm.startprocess.select_model") || "Seleziona un modello per visualizzare l'anteprima"}
          </Typography>
        </Stack>
      )}
    </>
  );

  const renderStep2Left = () => (
    <Stack spacing={isMobile ? 1.5 : 3}>
      {/* Process name field with bold label */}
      <Box>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontSize: isMobile ? 9 : 14,
            fontWeight: 800,
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: 0.6,
            mb: isMobile ? 0.5 : 2,
          }}
        >
          {getTranslation(translations, "pm.processstart.processname.label") || "Nome processo"}
        </Typography>
        <TextField
          id="startprocess-name"
          fullWidth
          inputProps={{ maxLength: 7000 }}
          variant="outlined"
          size="small"
          error={Boolean(touched.name && errors.name)}
          helperText={touched.name && errors.name}
          value={values.name}
          onChange={({ target }) => changeHandler("name", target.value)}
          InputProps={{ sx: { borderRadius: isMobile ? 2 : 5 } }}
        />
      </Box>

      {/* RichTextEditor */}
      <Box>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontSize: isMobile ? 9 : 14,
            fontWeight: 800,
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: 0.6,
            mb: isMobile ? 0.5 : 2,
          }}
        >
          {getTranslation(translations, "pm.startprocess.description") || "Descrizione dettagliata"}
        </Typography>
        <RichTextEditor
          value={values.description_detail || ""}
          onChange={(markdown) => {
            descriptionDetailRef.current = markdown;
            changeHandler("description_detail", markdown);
          }}
          minHeight={isDescriptionFocused ? 180 : 60}
          onFocus={() => setIsDescriptionFocused(true)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setIsDescriptionFocused(false);
            }
          }}
          sx={{
            width: "100%",
            "& [class*='_root_']": {
              fontSize: isMobile ? "0.875rem" : "0.9375rem",
            },
            "& [contenteditable]": {
              minHeight: isDescriptionFocused ? 180 : 60,
              fontSize: isMobile ? "0.875rem" : "0.9375rem",
            },
            "& [contenteditable] h1": { fontSize: isMobile ? "1.3rem" : "1.4rem" },
            "& [contenteditable] h2": { fontSize: isMobile ? "1.15rem" : "1.2rem" },
            "& [contenteditable] h3": { fontSize: isMobile ? "1rem" : "1.05rem" },
            "& [contenteditable] table": { fontSize: isMobile ? "0.875rem" : "0.9375rem" },
            "& .cm-editor": { fontSize: isMobile ? "0.875rem" : "0.9375rem" },
          }}
        />
      </Box>

      <Box>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontSize: isMobile ? 9 : 14,
            fontWeight: 800,
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: 0.6,
            mb: isMobile ? 0.5 : 2,
          }}
        >
          {getTranslation(translations, "settings.attachments.label")}
        </Typography>
        <DropZone files={selectedFiles} onFileChange={handleFileChange} maxFiles={10} />
        {/* Hidden input that receives the focus from the global
            Ctrl+V listener above. The synthesised paste event
            bubbles up to `document` where the DropZone listener
            handles it. No `onPaste` handler is wired here on
            purpose — we want the event to bubble untouched. */}
        <input
          ref={pasteCaptureRef}
          id="start-process-paste-capture"
          aria-hidden="true"
          tabIndex={-1}
          autoFocus
          style={{
            position: "absolute",
            width: 1,
            height: 1,
            opacity: 0,
            pointerEvents: "none",
            left: -1000,
            top: -1000,
          }}
        />
      </Box>
    </Stack>
  );

  const renderStep2Right = () => (
    <>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: isMobile ? 1 : 2 }}>
      {/* Labels card */}
      <Paper
        elevation={0}
        sx={{
          p: isMobile ? 1.25 : 2,
          borderRadius: isMobile ? 1.5 : 2,
          minWidth: 0,
          bgcolor: "background.paper",
          color: "text.primary",
          border: 1,
          borderColor: "divider",
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "none"
              : "0 1px 6px rgba(0,0,0,0.10)",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontSize: isMobile ? 9 : 14,
            fontWeight: 800,
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: 0.6,
            mb: isMobile ? 0.75 : 1.5,
          }}
        >
          {getTranslation(translations, "generic.labels.caption") || "Etichette"}
        </Typography>
        <FlowentiButton
          size="small"
          variant="outlined"
          component="label"
          onClick={() => setIsLabelManagerShown(true)}
          sx={{ minWidth: 0, width: "100%", height: 40 }}
        >
          {getTranslation(translations, "generic.labels.caption")}
        </FlowentiButton>
        <LabelManager
          checked={checked}
          setChecked={setChecked}
          open={isLabelManagerShown}
          handleClose={handleLabelManagerClose}
        />
      </Paper>

      {/* Priority card */}
      <Paper
        elevation={0}
        sx={{
          p: isMobile ? 1.25 : 2,
          borderRadius: isMobile ? 1.5 : 2,
          minWidth: 0,
          bgcolor: "background.paper",
          color: "text.primary",
          border: 1,
          borderColor: "divider",
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "none"
              : "0 1px 6px rgba(0,0,0,0.10)",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontSize: isMobile ? 9 : 14,
            fontWeight: 800,
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: 0.6,
            mb: isMobile ? 0.75 : 1.5,
          }}
        >
          {getTranslation(translations, "generic.priorities.caption") || "Priorità"}
        </Typography>
        <Box sx={{ maxWidth: 260 }}>
          <PrioritiesDropDown
            allPriorities={allPriorities}
            value={selectedPriority}
            onChange={(newPriority) => setSelectedPriority(newPriority)}
            administrator={administrator}
          />
        </Box>
      </Paper>
      </Box>

      {/* Fields card */}
      <Paper
        elevation={0}
        sx={{
          p: isMobile ? 1.25 : 2,
          borderRadius: isMobile ? 1.5 : 2,
          bgcolor: "background.paper",
          color: "text.primary",
          border: 1,
          borderColor: "divider",
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "none"
              : "0 1px 6px rgba(0,0,0,0.10)",
        }}
      >
        {/* Stessa struttura della sezione campi in TaskDetailDialog: accordion
            aperto di default, header con il titolo e l'icona informativa. */}
        <Accordion
          elevation={0}
          disableGutters
          defaultExpanded
          sx={{
            '&:before': {
              display: 'none',
            },
            boxShadow: 'none',
            '&.MuiAccordion-root': {
              border: 'none',
            },
            '&.Mui-expanded': {
              margin: 0,
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ArrowDropDown />}
            data-testid="cf-start-fields-header"
            sx={{ px: 0 }}
          >
            <Stack direction="row" alignItems="center" spacing={0}>
              <Typography
                variant="caption"
                sx={{
                  display: "inline-block",
                  fontSize: isMobile ? 9 : 14,
                  fontWeight: 800,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  lineHeight: 1.2,
                }}
              >
                {getTranslation(translations, "generic.user_data.caption") || "Dati utente"}
              </Typography>
              <UserDataFieldsInfo
                stopPropagation
                triggerSx={{ mt: 0.25 }}
              />
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0 }}>
        {/* Il pulsante "aggiungi userdata" è commentato di proposito.
            Serviva a creare una chiave libera valida solo per questa istanza di
            processo, e si poteva aprire anche quando il modello non dichiarava
            nessun campo. È stato tolto: due strade per aggiungere un dato (lo
            schema del modello e questo pulsante) chiedevano all'utente di
            sapere quale delle due stesse usando, e l'UI che ne risultava era più
            complicata di quanto il caso valesse. Il valore aggiuntivo per la
            singola istanza non è più raggiungibile da qui.
            La meccanica sotto (`FieldsList`, gli `openAddFieldDialog` e il
            dialog ad-hoc) resta intatta: si riaccende rimettendo il pulsante. */}
        {/*
        <FlowentiButton
          id="addfield-button"
          startIcon={<AddCircleOutlineRounded />}
          size="small"
          variant="outlined"
          onClick={() => setOpenAddFieldDialog(true)}
          sx={{ mt: isMobile ? 0.75 : 1.5, minWidth: 140, maxWidth: 260 }}
        >
          {getTranslation(translations, "generic.user_data.caption")}
        </FlowentiButton>
        */}
        {selectedProcess && (
          <Box mt={isMobile ? 1 : 2}>
            <FieldsList
              linkChange
              enableUndefined
              handleSaveUserdata={async (newValues) => changeHandler("meta", newValues)}
              selectedProcess={selectedProcess?.id}
              // Gli schemi del modello restano il percorso nuovo (sopra, dal
              // contesto del server): qui la lista rende solo le chiavi ad-hoc.
              schema={undefined}
              definitions={startContext?.fields.map((field) => field.definition)}
              modelId={String(selectedProcess.idprocessmodel)}
              pendingFiles={fieldFiles}
              onStageFiles={stageFieldFiles}
              onDiscardFiles={(fieldId) => setFieldFiles((current) => {
                const { [fieldId]: discarded, ...remaining } = current;
                return remaining;
              })}
              onRemoveFile={(fieldId, fileName) => setFieldFiles((current) => ({
                ...current,
                [fieldId]: (current[fieldId] ?? []).filter((file) => file.name !== fileName),
              }))}
              openAddFieldDialog={openAddFieldDialog}
              onAddFieldDialogClose={() => setOpenAddFieldDialog(false)}
              value={values.meta}
              onChange={(newValues) => changeHandler("meta", newValues)}
            />
          </Box>
        )}
        {startContext ? (
          // L'`onBlur` sta sul contenitore: il blur di React bolle, quindi un
          // solo appiglio copre ogni campo senza toccare `CustomFieldInput`.
          // Un campo in sola lettura non prende il focus, quindi gli unici
          // blur che arrivano qui sono di campi che si potevano scrivere.
          <Stack spacing={2} mt={isMobile ? 1 : 2} onBlur={scheduleStartContextRefresh}>
            {startContext.fields.map((field) => {
              const staged = fieldFiles[field.fieldId] ?? [];
              const alreadyPopulated =
                staged.length > 0 ||
                (Array.isArray(field.value) && field.value.length > 0);
              return (
                <Stack key={field.fieldId} direction="row" alignItems="center" spacing={1}>
                  <Box maxWidth="100%" flex={1} minWidth={0}>
                    {field.definition.type === "attachment" && field.access === "editable" ? (
                      <Box data-testid={`cf-value-${field.fieldId}`}>
                        <Typography variant="caption" color="text.secondary">{field.definition.label}</Typography>
                        {!alreadyPopulated && (
                        <Box>
                          <Button component="label" size="small" variant="outlined" startIcon={<AttachFile fontSize="small" />}>
                            {getTranslation(translations, "custom_fields.attachment.add")}
                            <input
                              hidden
                              multiple
                              type="file"
                              accept={SUPPORTED_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
                              data-testid={`cf-value-${field.fieldId}-input`}
                              onChange={(event) => {
                                stageFieldFiles(field.fieldId, event.target.files);
                                event.target.value = "";
                              }}
                            />
                          </Button>
                        </Box>
                        )}
                        {/* Gli allegati qui sono resi inline, non via CustomFieldInput:
                            l'errore del server va mostrato qui, a mano. */}
                        {errorForField(field.fieldId) && (
                          <Typography color="error" variant="caption" component="div">
                            {errorForField(field.fieldId)}
                          </Typography>
                        )}
                        {(fieldFiles[field.fieldId] ?? []).map((file) => (
                          <Stack key={file.name} direction="row" alignItems="center" spacing={1}>
                            <Typography variant="body2">{file.name}</Typography>
                            <IconButton
                              size="small"
                              aria-label={`${getTranslation(translations, "generic.button.delete")} ${file.name}`}
                              onClick={() => {
                                setFieldFiles((current) => ({
                                  ...current,
                                  [field.fieldId]: (current[field.fieldId] ?? []).filter((item) => item.name !== file.name),
                                }));
                                clearFieldError(field.fieldId);
                              }}
                            >
                              <Close fontSize="small" />
                            </IconButton>
                          </Stack>
                        ))}
                      </Box>
                    ) : (
                      <CustomFieldInput
                        field={field}
                        value={startValues[field.fieldId] ?? null}
                        // Il rifiuto del server su questo campo (validazione 422). Il
                        // messaggio e' risolto dal fieldId.
                        error={errorForField(field.fieldId)}
                        onChange={(next) => {
                          setStartValues((previous) => ({ ...previous, [field.fieldId]: next }));
                          // L'utente sta correggendo: l'errore di quel campo non e' piu'
                          // attendibile. Gli altri restano.
                          clearFieldError(field.fieldId);
                        }}
                      />
                    )}
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        ) : startContextError ? (
          <Typography color="error">{getTranslation(translations, "generic.error.caption") || "Impossibile caricare i campi del modello"}</Typography>
        ) : selectedProcess && (
          <Typography>Caricamento campi...</Typography>
        )}
          </AccordionDetails>
        </Accordion>
      </Paper>

      {/* SLA (spec SLA_UI_Impact_Spec sez. 4; piano Client sez. 5 F2b): sotto
          "Dati utente", chiusa di default sullo SLA del processo; l'admin la
          apre per l'override relativo/assoluto. La bozza viene unita al POST
          di avvio: nessuna seconda chiamata. Nascosta se lo SLA
          dell'organizzazione e' disattivo. */}
      {slaEnabled && selectedProcess && selectedOrganization?.id && (
        <Paper
          elevation={0}
          sx={{
            p: isMobile ? 1.25 : 2,
            borderRadius: isMobile ? 1.5 : 2,
            bgcolor: "background.paper",
            color: "text.primary",
            border: 1,
            borderColor: "divider",
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "none"
                : "0 1px 6px rgba(0,0,0,0.10)",
          }}
        >
          <SlaStartSection
            idorganization={selectedOrganization.id}
            idprocessmodel={selectedProcess.idprocessmodel}
            processName={selectedProcess.processmodelname}
            isAdmin={isAdmin}
            disabled={loading}
            onChange={handleSlaChange}
          />
        </Paper>
      )}
    </>
  );

  return (
    <>
    <Dialog
      open={Boolean(open)}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      onClose={(_, reason) => {
        if (reason === "backdropClick") return;
        setOpen(false);
      }}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          height: isMobile ? "100%" : "80vh",
          maxHeight: isMobile ? "100%" : "80vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, borderBottom: 1, borderColor: "divider" }}>
        <Stack direction={isMobile ? "column" : "row"} justifyContent="space-between" alignItems={isMobile ? "stretch" : "center"} spacing={isMobile ? 1.5 : 2}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6">
                {getTranslation(translations, "pm.startprocess.label.caption")}
              </Typography>
              <Labels labelCompleteMode labels={checked} />
            </Stack>
            {isMobile && (
              <IconButton onClick={() => setOpen(false)} sx={{ borderRadius: 2, p: 0.5 }}>
                <Close />
              </IconButton>
            )}
          </Stack>
          {step === 1 && (
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: isMobile ? undefined : 1, minWidth: 0, maxWidth: isMobile ? "100%" : 360, px: 1.5, py: 0.5, bgcolor: "rgba(84, 189, 193, 0.08)", border: "1px solid rgba(84, 189, 193, 0.4)", borderRadius: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" color="primary" fontWeight={600} noWrap sx={{ display: "block", fontSize: "0.65rem" }}>
                  {getTranslation(translations, "pm.startprocess.selected_model") || "Modello selezionato"}
                </Typography>
                <Typography variant="body2" fontWeight={600} noWrap title={selectedProcess ? processLabel(selectedProcess) : ""}>
                  {selectedProcess ? processLabel(selectedProcess) : ""}
                </Typography>
              </Box>
              <Button size="small" onClick={() => setStep(0)} sx={{ flexShrink: 0, minWidth: 0, p: 0.5, fontSize: "0.72rem", color: "primary.dark" }}>
                {getTranslation(translations, "pm.startprocess.change_model") || "Cambia modello"}
              </Button>
            </Stack>
          )}
          <Stack direction="row" alignItems="center" spacing={2} justifyContent={isMobile ? "center" : "flex-end"}>
            <Stepper
              activeStep={step}
              sx={{
                minWidth: isMobile ? "unset" : 340,
                width: isMobile ? "100%" : "auto",
                "& .MuiStepLabel-label": {
                  color: "#54bdc1 !important",
                  fontWeight: 600,
                  fontSize: isMobile ? "0.75rem" : "inherit",
                  "&.Mui-active": {
                    fontWeight: 700,
                  },
                  "&.Mui-completed": {
                    fontWeight: 700,
                  },
                },
                "& .MuiStepConnector-line": {
                  borderColor: "#54bdc1",
                  borderTopWidth: 2,
                },
                "& .MuiStepIcon-root": {
                  width: isMobile ? 18 : 27,
                  height: isMobile ? 18 : 27,
                  overflow: "visible",
                  "& circle": {
                    fill: "#fff",
                    stroke: "#e0e0e0",
                    strokeWidth: 1.5,
                  },
                  "& .MuiStepIcon-text": {
                    fill: "#e0e0e0",
                    fontWeight: "bold",
                    fontSize: isMobile ? "0.55rem" : "0.95rem",
                  },
                  "&.Mui-active": {
                    "& circle": {
                      fill: "#fff",
                      stroke: "#54bdc1",
                      strokeWidth: 2,
                    },
                    "& .MuiStepIcon-text": {
                      fill: "#54bdc1",
                      fontWeight: "bold",
                    },
                  },
                  "&.Mui-completed": {
                    "& circle": {
                      fill: "#fff",
                      stroke: "#54bdc1",
                      strokeWidth: 1.5,
                    },
                    "& path": {
                      fill: "#54bdc1",
                    },
                  },
                },
              }}
            >
              <Step>
                <StepLabel>{getTranslation(translations, "pm.startprocess.step1") || "Scelta Modello"}</StepLabel>
              </Step>
              <Step>
                <StepLabel>{getTranslation(translations, "pm.startprocess.step2") || "Dettagli"}</StepLabel>
              </Step>
            </Stepper>
            {!isMobile && (
              <IconButton onClick={() => setOpen(false)} sx={{ borderRadius: 2 }}>
                <Close />
              </IconButton>
            )}
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          p: 0,
        }}
      >
        {processes.length === 0 ? (
          <Stack direction="column" alignItems="center" justifyContent="center" spacing={2} sx={{ flex: 1 }}>
            <Box component="img" alt="..." sx={{ width: isMobile ? "220px" : "308px", height: isMobile ? "220px" : "308px" }} src={noModel} />
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              <Typography color="textSecondary" component="p" sx={{ mt: 1, fontSize: "1.2rem", fontWeight: "bold" }}>
                {getTranslation(translations, "generic.create_one_model.caption")}
              </Typography>
              {isAdmin && (
                <Button onClick={() => navigate("/dashboard/process/insert")} variant="outlined" size="large" sx={{ mt: 3, borderRadius: 5 }}>
                  {getTranslation(translations, "pm.label.button.process_name")}
                </Button>
              )}
            </Box>
          </Stack>
        ) : (
          <>
            {/* STEP 1: model selection + BPMN preview */}
            {step === 0 && isFullScreen ? (
              <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                {renderStep1List()}
              </Box>
            ) : step === 0 && (
              isMobile ? (
                <Box sx={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
                  {/* Left panel: process list */}
                  <Box sx={{ height: "45%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    {renderStep1List()}
                  </Box>

                  {/* Divider */}
                  <Box sx={{ height: "1px", bgcolor: "divider" }} />

                  {/* Right panel: BPMN preview */}
                  <Box
                    sx={{
                      height: "55%",
                      position: "relative",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark" ? "#000000" : "background.paper",
                    }}
                  >
                    {renderStep1Preview()}
                  </Box>
                </Box>
              ) : (
                <PanelGroup orientation="horizontal" style={{ flex: 1, minHeight: 0 }} defaultLayout={step1DefaultLayout} onLayoutChanged={(layout) => saveLayout(STEP1_LAYOUT_KEY, layout)}>
                  {/* Left panel: process list */}
                  <Panel id="step1-list" defaultSize="10%" minSize="8%" maxSize="50%" style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    {renderStep1List()}
                  </Panel>

                  {/* Splitbar */}
                  <PanelResizeHandle className="panel-resize-handle" style={{
                    width: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "col-resize",
                    transition: "background 0.15s",
                  }}>
                    <Box
                      sx={{
                        width: 6,
                        height: 38,
                        borderRadius: 4,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "#ffffff" : "action.hover",
                        py: 0.75,
                        transition: "background-color 0.15s",
                      }}
                    >
                      {[0, 1, 2].map((dot) => (
                        <Box
                          key={dot}
                          sx={{
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            backgroundColor: (theme) =>
                              theme.palette.mode === "dark" ? "#000000" : "text.disabled",
                          }}
                        />
                      ))}
                    </Box>
                  </PanelResizeHandle>

                  {/* Right panel: BPMN preview */}
                  <Panel id="step1-preview" defaultSize="70%" style={{ position: "relative", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                    {renderStep1Preview()}
                  </Panel>
                </PanelGroup>
              )
            )}

            {/* STEP 2: details form */}
            {step === 1 && (
              <Box sx={{ flex: 1, overflow: "auto", p: isMobile ? 2 : 3 }}>
                {isMobile ? (
                  <Stack
                    spacing={1}
                    sx={{
                      flex: 1,
                      overflowY: "auto",
                      p: 1,
                      "&::-webkit-scrollbar": { display: "none" },
                      scrollbarWidth: "none",
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.25,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1.5,
                        bgcolor: "background.paper",
                      }}
                    >
                      {renderStep2Left()}
                    </Box>
                    <Stack spacing={1}>{renderStep2Right()}</Stack>
                  </Stack>
                ) : (
                  <PanelGroup orientation="horizontal" style={{ flex: 1, overflow: "hidden", height: "100%" }} defaultLayout={loadLayout(STEP2_LAYOUT_KEY)} onLayoutChanged={(layout) => saveLayout(STEP2_LAYOUT_KEY, layout)}>
                    {/* Left panel 60%: name, description, attachments */}
                    <Panel id="step2-left" defaultSize="60%" minSize="35%" style={{ display: "flex", flexDirection: "column", overflow: "auto", padding: "24px 16px 24px 24px" }}>
                      {renderStep2Left()}
                    </Panel>

                    {/* Splitbar */}
                    <PanelResizeHandle className="panel-resize-handle" style={{
                      width: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "col-resize",
                      transition: "background 0.15s",
                    }}>
                      <Box
                        sx={{
                          width: 6,
                          height: 38,
                          borderRadius: 4,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.5,
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark" ? "#ffffff" : "action.hover",
                          py: 0.75,
                          transition: "background-color 0.15s",
                        }}
                      >
                        {[0, 1, 2].map((dot) => (
                          <Box
                            key={dot}
                            sx={{
                              width: 5,
                              height: 5,
                              borderRadius: "50%",
                              backgroundColor: (theme) =>
                                theme.palette.mode === "dark" ? "#000000" : "text.disabled",
                            }}
                          />
                        ))}
                      </Box>
                    </PanelResizeHandle>

                    {/* Right panel 40%: labels, priorities, fields */}
                    <Panel id="step2-right" defaultSize="40%" minSize="25%" style={{ display: "flex", flexDirection: "column", overflow: "auto", padding: "24px 24px 24px 16px", gap: 16 }}>
                      {renderStep2Right()}
                    </Panel>
                  </PanelGroup>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      {/* L'ultimo stato dichiarato dal server, mentre il modulo e' ancora
          aperto: dopo un timeout e' l'unica cosa che separa "non lo so" da
          "e' partito", e un rifiuto si legge qui. */}
      {operationStatus && (
        <Box sx={{ px: isMobile ? 2 : 3, pb: 1 }}>
          <OperationStatusLine status={operationStatus} />
        </Box>
      )}
      {attachmentRecoveryError && (
        <Box sx={{ px: isMobile ? 2 : 3, pb: 1 }}>
          <Typography color="error" role="alert">{attachmentRecoveryError}</Typography>
          <Button onClick={() => void recoverStartAttachments()} disabled={loading}>
            {getTranslation(translations, "generic.retry.caption") || "Riprova"}
          </Button>
        </Box>
      )}

      {processes.length > 0 && (
        <DialogActions sx={{ px: isMobile ? 2 : 3, py: 2, borderTop: 1, borderColor: "divider" }}>
          {step === 0 ? (
            <Stack direction="row" justifyContent="space-between" width="100%" spacing={2}>
              <FlowentiButton
                id="cancel-button-process"
                variant="outlined"
                size="medium"
                onClick={() => setOpen(false)}
                sx={{ flex: isMobile ? 1 : "none" }}
              >
                {getTranslation(translations, "pm.startprocess.button.cancel.caption")}
              </FlowentiButton>
              <FlowentiButton
              id="next-button-process"
                variant="contained"
                color="primary"
                size="medium"
                disabled={!selectedProcess}
                onClick={() => setStep(1)}
                sx={{ borderRadius: 5, flex: isMobile ? 1 : "none" }}
              >
                {getTranslation(translations, "generic.next.caption") || "Continua"}
              </FlowentiButton>
            </Stack>
          ) : (
            isMobile ? (
              <Stack spacing={1.5} width="100%">
                <Stack direction="row" spacing={2} width="100%">
                  <FlowentiButton
                    variant="outlined"
                    size="medium"
                    onClick={() => {
                      setStep(0);
                      setTimeout(() => scrollToSelectedProcess(), 150);
                    }}
                    sx={{ flex: 1 }}
                  >
                    {getTranslation(translations, "generic.back.caption") || "Indietro"}
                  </FlowentiButton>
                  <FlowentiButton
                    id="startprocess-button2"
                    variant="contained"
                    color="primary"
                    size="medium"
                    // `canSubmit` oltre al form: due clic ravvicinati sono due
                    // intenti, e da `unknown` — un timeout di cui non si sa
                    // l'esito — ripartire sarebbe preparare alla cieca su un
                    // numero che potrebbe essere gia' stato emesso.
                    disabled={
                      !isValid || !selectedProcess || !startContext || loading || !canSubmit(outcome)
                    }
                    onClick={() => handleStartProcess({ ...values, files: selectedFiles })}
                    sx={{ borderRadius: 5, flex: 1 }}
                  >
                    {getTranslation(translations, "pm.startprocess.button.ok.caption")}
                  </FlowentiButton>
                </Stack>
                <FlowentiButton
                  id="cancel-button-process"
                  variant="text"
                  size="medium"
                  onClick={() => setOpen(false)}
                  fullWidth
                >
                  {getTranslation(translations, "pm.startprocess.button.cancel.caption")}
                </FlowentiButton>
              </Stack>
            ) : (
              <Stack direction="row" justifyContent="space-between" width="100%">
                <FlowentiButton
                  id="cancel-button-process"
                  variant="outlined"
                  size="medium"
                  onClick={() => setOpen(false)}
                >
                  {getTranslation(translations, "pm.startprocess.button.cancel.caption")}
                </FlowentiButton>
                <Stack direction="row" spacing={2}>
                  <FlowentiButton
                    variant="outlined"
                    size="medium"
                    onClick={() => {
                      setStep(0);
                      setTimeout(() => scrollToSelectedProcess(), 150);
                    }}
                  >
                    {getTranslation(translations, "generic.back.caption") || "Indietro"}
                  </FlowentiButton>
                  <FlowentiButton
                    id="startprocess-button2"
                    variant="contained"
                    color="primary"
                    size="medium"
                    // `canSubmit` oltre al form: due clic ravvicinati sono due
                    // intenti, e da `unknown` — un timeout di cui non si sa
                    // l'esito — ripartire sarebbe preparare alla cieca su un
                    // numero che potrebbe essere gia' stato emesso.
                    disabled={
                      !isValid || !selectedProcess || !startContext || loading || !canSubmit(outcome)
                    }
                    onClick={() => handleStartProcess({ ...values, files: selectedFiles })}
                    sx={{ borderRadius: 5 }}
                  >
                    {getTranslation(translations, "pm.startprocess.button.ok.caption")}
                  </FlowentiButton>
                </Stack>
              </Stack>
            )
          )}
        </DialogActions>
      )}
    </Dialog>

      {/* La conferma quando i codici sono gia' usati. Sta fuori dal dialogo di
          avvio perche' non e' un passo dell'avvio: e' la domanda che il motore
          fa, e va mostrata anche se il dialogo di avvio si e' chiuso. */}
      <DuplicateCodeDialog
        // La sfida e' aperta finche' c'e' una sfida: si chiude dal titolo, e
        // non dall'esito. Chiuderla su `applied` nasconderebbe la prova che
        // l'avvio e' avvenuto, e su un rifiuto direbbe il falso per omissione.
        open={challengeId !== null}
        outcome={outcome}
        codes={duplicateCodes}
        status={operationStatus}
        onConfirm={handleConfirmDuplicate}
        onCancel={() => {
          setOutcome({ kind: "idle" });
          setDuplicateCodes([]);
          setChallengeId(null);
          setConfirmationToken(null);
          setOperationStatus(null);
          // L'intento e' annullato: il prossimo avvio ne avra' uno nuovo.
          setOperationId(null);
        }}
      />

      {/* Fullscreen BPMN overlay */}
      {isFullScreen && selectedProcess && step === 0 && (
        <Box
          ref={fullscreenRef}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            bgcolor: (theme) =>
              theme.palette.mode === "dark" ? "#000000" : "background.paper",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", p: 1, borderBottom: 1, borderColor: "divider" }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {processLabel(selectedProcess)}
            </Typography>
            <IconButton size="small" onClick={async () => {
              setIsFullScreen(false);
              try {
                if (document.fullscreenElement) await document.exitFullscreen();
                window.screen.orientation?.unlock?.();
              } catch {}
            }} sx={{ borderRadius: 2, ml: 1 }}>
              <FullscreenExit />
            </IconButton>
          </Box>
          <Box
            ref={bpmnContainerRef}
            sx={getBpmnPreviewSx(isMobile)}
          />
          {/* Zoom controls in fullscreen */}
          <Box sx={{ position: "absolute", bottom: isMobile ? 48 : 12, right: 12, display: "flex", alignItems: "center", gap: 0.5, bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 5, px: isMobile ? 1.5 : 1, py: isMobile ? 1 : 0.5, zIndex: 10 }}>
            <IconButton
              size={isMobile ? "medium" : "small"}
              sx={{ borderRadius: 2, p: isMobile ? 1 : 0.5 }}
              onClick={() => {
                const newZoom = Math.max(0.2, parseFloat((zoomLevel - 0.15).toFixed(2)));
                bpmnViewerRef.current?.get("canvas").zoom(newZoom);
                setZoomLevel(newZoom);
              }}
            >
              <ZoomOut fontSize={isMobile ? "medium" : "small"} />
            </IconButton>
            <Tooltip title={getTranslation(translations, "pm.startprocess.fit_viewport") || "Adatta allo schermo"}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", px: 0.5 }}
                onClick={() => {
                  bpmnViewerRef.current?.get("canvas").zoom("fit-viewport");
                  setZoomLevel(1);
                }}
              >
                <SearchIcon sx={{ fontSize: isMobile ? 18 : 14, color: "text.secondary" }} />
                <Typography variant="caption" sx={{ minWidth: 32, textAlign: "center", fontWeight: 600, fontSize: isMobile ? "0.8rem" : "inherit" }}>
                  {Math.round(zoomLevel * 100)}%
                </Typography>
              </Box>
            </Tooltip>
            <IconButton
              size={isMobile ? "medium" : "small"}
              sx={{ borderRadius: 2, p: isMobile ? 1 : 0.5 }}
              onClick={() => {
                const newZoom = Math.min(3.0, parseFloat((zoomLevel + 0.15).toFixed(2)));
                bpmnViewerRef.current?.get("canvas").zoom(newZoom);
                setZoomLevel(newZoom);
              }}
            >
              <ZoomIn fontSize={isMobile ? "medium" : "small"} />
            </IconButton>
          </Box>
        </Box>
      )}
    </>
  );
};

export default StartProcess;
